import { plural, t } from "@lingui/macro";

import { Item } from "@whoosh/design-system/src/components/controls/DropdownSelect/components/Item";
import { Trigger } from "@whoosh/design-system/src/components/controls/Trigger/Trigger";
import { Menu } from "@whoosh/design-system/src/components/overlay/Menu";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { NotificationChannel } from "./hooks/__generated__/useSetUserNotificationPreferencesMutation.graphql";

type NotificationOption = {
  label: string;
  value: NotificationChannel | "OFF";
  showUnderline?: boolean;
  isDisabled?: boolean;
};

type ChannelOption = NotificationOption & { value: NotificationChannel };

const isChannelOption = (option: NotificationOption): option is ChannelOption =>
  option.value !== "OFF";

type NotificationRowProps = {
  label: string;
  values: NotificationChannel[];
  options: NotificationOption[];
  onChange: (values: NotificationChannel[]) => void;
  showGuide?: boolean;
  testID?: string;
};

export const NotificationRow = ({
  label,
  values,
  options,
  onChange,
  showGuide,
  testID,
}: NotificationRowProps) => {
  const selectableOptions = options.filter(isChannelOption);
  const turnOffOption = options.find((option) => option.value === "OFF");
  const disabledValues = new Set(
    options.filter((option) => option.isDisabled).map((option) => option.value)
  );

  const isSelected = (value: NotificationChannel) => values.includes(value);
  const selectedCount = values.length;

  const displayValue =
    selectedCount === 0
      ? t`Off`
      : selectedCount === 1
      ? (() => {
          const singleValue = values[0];
          if (singleValue === "EMAIL") return t`Email`;
          if (singleValue === "SMS") return t`SMS`;
          return (
            selectableOptions.find((option) => option.value === singleValue)
              ?.label ?? t`Selected`
          );
        })()
      : plural(selectedCount, {
          one: "# selected",
          other: "# selected",
        });

  const triggerSelectedItem = {
    label: displayValue,
    value: displayValue,
  };

  const handleToggle = (value: NotificationChannel) => {
    if (disabledValues.has(value)) return;
    const nextValues = values.includes(value)
      ? values.filter((val) => val !== value)
      : [...values, value];
    onChange(nextValues);
  };

  const handleTurnOff = () => {
    onChange([]);
  };

  return (
    <HStack
      py={1}
      columnGap={4}
      justifyContent="space-between"
      alignItems="center"
    >
      <Text variant="mainSmallMedium" flex={1} pr={3}>
        {showGuide ? <GreenDot /> : null} {label}
      </Text>
      <Box>
        <Menu
          showActionSheetDoneButton={false}
          trigger={({ onPress, isOpen }) => (
            <Trigger
              size="regular"
              onPress={onPress}
              placeholder={t`Off`}
              selectedItem={triggerSelectedItem}
              includeLeftElementInSelectedValue={false}
              hideChevron={false}
              hideLabelInDisplay={false}
              hideSubLabelInDisplay={false}
              variant="compact"
              isOpened={isOpen}
              testID={testID}
            />
          )}
          content={({ closeMenu }) => (
            <Menu.Content size="large">
              <VStack>
                {selectableOptions.map((option, index) => (
                  <Item
                    key={`${option.value}-${index}`}
                    isMulti
                    item={option}
                    isSelected={isSelected(option.value)}
                    onPress={() => handleToggle(option.value)}
                    testID={
                      testID
                        ? `${testID}-notification-option-${option.value}`
                        : `notification-option-${option.value}`
                    }
                  />
                ))}
                {turnOffOption ? (
                  <Pressable
                    onPress={() => {
                      handleTurnOff();
                      closeMenu();
                    }}
                    paddingHorizontal={3}
                    paddingVertical={4}
                  >
                    <Text variant="mainBaseSemiBold" color="text-primary">
                      {turnOffOption.label}
                    </Text>
                  </Pressable>
                ) : null}
              </VStack>
            </Menu.Content>
          )}
        />
      </Box>
    </HStack>
  );
};

const GreenDot = () => {
  return (
    <Box
      width={6}
      height={6}
      backgroundColor="text-success"
      borderRadius="2"
      position="absolute"
      left={-12}
      top={5}
    />
  );
};
